/**
 * connector-targets.json builder (P4, 2026-06-16).
 *
 * Turns a friendly form into the exact `connector-targets.json` the edge
 * scanner reads, so operators do not hand-write JSON ("not everyone understands
 * JSON"). Pure + unit-tested; the UI (ConnectorBuilder.tsx) is a thin shell
 * over this. Shapes match apps/edge-discovery/src/connector.rs.
 */

export type TargetKind =
  | 'smb_server'
  | 'local_filesystem'
  | 'smb_share'
  | 'postgres'
  | 'mysql'
  | 'mssql'
  | 'oracle'
  | 's3'
  | 'db2_for_i';

export interface TargetRow {
  readonly kind: TargetKind;
  readonly fields: Record<string, string>;
}

/** Human label + the required field keys for each kind (drives the form + validation). */
export const TARGET_SPECS: Record<TargetKind, { label: string; required: string[]; optional: string[] }> = {
  smb_server: { label: 'File server (whole server, all shares)', required: ['host'], optional: [] },
  local_filesystem: { label: 'Folder / share path', required: ['root'], optional: [] },
  smb_share: { label: 'Single SMB share', required: ['host', 'share'], optional: ['username', 'password', 'domain', 'path'] },
  postgres: { label: 'PostgreSQL', required: ['dsn', 'schema', 'table', 'column'], optional: [] },
  mysql: { label: 'MySQL / MariaDB', required: ['dsn', 'schema', 'table', 'column'], optional: [] },
  mssql: { label: 'SQL Server / Azure SQL', required: ['host', 'username', 'password', 'database', 'table', 'column'], optional: ['port', 'schema'] },
  oracle: { label: 'Oracle', required: ['connect_string', 'username', 'password', 'schema', 'table', 'column'], optional: [] },
  s3: { label: 'S3 bucket', required: ['bucket', 'region'], optional: ['prefix', 'endpoint_url'] },
  db2_for_i: { label: 'Db2 for i (IBM i / AS/400)', required: ['system', 'username', 'password_credential'], optional: ['library', 'iasp', 'driver_name', 'hex_field_ccsid', 'use_tls'] },
};

const trimmed = (v: string | undefined) => (v ?? '').trim();

/** Build ONE connector target object, or null if a required field is missing. */
export function buildConnectorTarget(row: TargetRow): Record<string, unknown> | null {
  const spec = TARGET_SPECS[row.kind];
  if (!spec) return null;
  for (const k of spec.required) {
    if (!trimmed(row.fields[k])) return null;
  }
  const f = (k: string) => trimmed(row.fields[k]);
  const withOptional = (base: Record<string, unknown>, keys: string[]) => {
    for (const k of keys) {
      const v = f(k);
      if (v) base[k] = k === 'port' ? Number(v) : v;
    }
    return base;
  };

  switch (row.kind) {
    case 'smb_server':
      return { kind: 'smb_server', host: f('host') };
    case 'local_filesystem':
      return { kind: 'local_filesystem', root: f('root') };
    case 'smb_share':
      return withOptional({ kind: 'smb_share', host: f('host'), share: f('share') }, spec.optional);
    case 'postgres':
      return { kind: 'postgres', dsn: f('dsn'), schema: f('schema'), table: f('table'), column: f('column') };
    case 'mysql':
      return { kind: 'mysql', dsn: f('dsn'), schema: f('schema'), table: f('table'), column: f('column') };
    case 'mssql':
      // The edge variant is `ms_sql_server`.
      return withOptional(
        { kind: 'ms_sql_server', host: f('host'), username: f('username'), password: f('password'), database: f('database'), table: f('table'), column: f('column') },
        spec.optional,
      );
    case 'oracle':
      return { kind: 'oracle', connect_string: f('connect_string'), username: f('username'), password: f('password'), schema: f('schema'), table: f('table'), column: f('column') };
    case 's3':
      return withOptional({ kind: 's3', bucket: f('bucket'), region: f('region') }, spec.optional);
    case 'db2_for_i': {
      // The password lives on the agent host; this file names it. See
      // @/lib/edge-scan-targets for the same rule in the console form.
      if (!/^[a-z0-9][a-z0-9_-]{0,63}$/.test(f('password_credential'))) return null;
      const t: Record<string, unknown> = { kind: 'db2_for_i', system: f('system'), username: f('username'), password_credential: f('password_credential') };
      // IBM i catalogue names are uppercase and the agent binds `library` into
      // `WHERE TABLE_SCHEMA = ?`, so a lowercase-typed library never matches.
      if (f('library')) t['library'] = f('library').toUpperCase();
      for (const k of ['iasp', 'driver_name']) if (f(k)) t[k] = f(k);
      if (f('hex_field_ccsid')) {
        // A bad value here would serialise as JSON `null` (NaN -> null), and
        // the agent deserialises hex_field_ccsid as u16, so one bad target
        // fails the whole connector-targets.json, not just this row.
        const ccsid = Number(f('hex_field_ccsid'));
        if (!Number.isInteger(ccsid) || ccsid < 1 || ccsid > 65534) return null;
        t['hex_field_ccsid'] = ccsid;
      }
      if (f('use_tls')) t['use_tls'] = f('use_tls').toLowerCase() !== 'false';
      return t;
    }
    default:
      return null;
  }
}

/** Build the full array, dropping incomplete rows. */
export function buildConnectorTargets(rows: readonly TargetRow[]): Record<string, unknown>[] {
  return rows.map(buildConnectorTarget).filter((t): t is Record<string, unknown> => t !== null);
}

/** Serialize to the connector-targets.json text. */
export function serializeTargets(rows: readonly TargetRow[]): string {
  return JSON.stringify(buildConnectorTargets(rows), null, 2) + '\n';
}
